/* Which routes are applications, and which files each one is built from.
 *
 * CommonJS on purpose: gatsby-node.js reads this same list at build time to
 * write out the source each About window fetches. One list means an app can't
 * end up on the desktop showing somebody else's source, and a file renamed
 * without updating this fails the build instead of failing in the browser.
 *
 * `origin` is the pen the app grew out of, where there was one. It's what the
 * About box means by "originally": these all started as CodePens. Its `path`
 * is the post that pen was embedded in, now deleted, which gatsby-node.js
 * redirects to the app that replaced it.
 */

const APPS = {
  "/whiteboard": {
    name: "Whiteboard",
    blurb: "Draw, erase, save boards to this browser, export a PNG.",
    origin: {
      title: "canvas html 5 whiteboard",
      date: "2022-01-22",
      path: "/blog/2022-01-22/canvas-white-board/",
    },
    files: ["src/pages/whiteboard.js", "src/styles/whiteboard.css"],
  },
  "/terminal": {
    name: "Terminal",
    blurb:
      "A shell over the posts on this site. `ls`, `cd`, `cat`, and friends.",
    files: ["src/pages/terminal.js", "src/styles/terminal.css"],
  },
  "/neo-tracker": {
    name: "NEO Tracker",
    blurb: "Near-Earth objects for a given week, from NASA's open endpoint.",
    files: ["src/pages/neo-tracker.js", "src/styles/neo-tracker.css"],
  },
  "/weather": {
    name: "Weather",
    blurb: "Search a city, get conditions and five days. Music optional.",
    files: [
      "src/pages/weather.js",
      "src/utils/weatherJazz.js",
      "src/styles/weather.css",
    ],
  },
  "/pixel-to-rem": {
    name: "Pixels to Rem",
    blurb: "Convert either direction, against whatever root size you're on.",
    origin: {
      title: "pixels to rem conversion",
      date: "2022-02-17",
      path: "/blog/2022-02-17/pixel-to-rem/",
    },
    files: ["src/pages/pixel-to-rem.js", "src/styles/pixel-to-rem.css"],
  },
  "/quit-smoking": {
    name: "Quit Smoking",
    blurb: "What the habit costs a day, a year, and over the years so far.",
    origin: {
      title: "smoking cessation savings calculator",
      date: "2022-01-18",
      path: "/blog/2022-01-18/quit-smoking/",
    },
    files: ["src/pages/quit-smoking.js", "src/styles/quit-smoking.css"],
  },
  "/budget": {
    name: "Budget",
    blurb: "Money in, money out, what's left. Kept in this browser.",
    origin: {
      title: "budget calculator",
      date: "2022-04-17",
      path: "/blog/2022-04-14/budget-calculator/",
    },
    files: ["src/pages/budget.js", "src/styles/budget.css"],
  },
  "/json-schema": {
    name: "Schema Generator",
    blurb: "Paste a payload, get a JSON Schema for it.",
    origin: {
      title: "schema generator",
      date: "2023-02-21",
      path: "/blog/2023-02-21/json-schema-generator/",
    },
    files: [
      "src/pages/json-schema.js",
      "src/utils/jsonSchema.js",
      "src/styles/json-tool.css",
    ],
  },
  "/json-diff": {
    name: "JSON Deep Diff",
    blurb: "Two payloads in, every path that differs out.",
    origin: {
      title: "json deep diff",
      date: "2023-06-12",
      path: "/blog/2023-06-12/json-deep-diff/",
    },
    files: [
      "src/pages/json-diff.js",
      "src/utils/jsonDiff.js",
      "src/styles/json-tool.css",
    ],
  },
  "/rpsls": {
    name: "Rock Paper Scissors Lizard Spock",
    blurb: "Five throws, ten outcomes, and a verb for every one of them.",
    origin: {
      title: "rock, paper, scissors, lizard, spock",
      date: "2021-02-27",
      path: "/blog/2021-02-27/rock-paper-scissors-lizard-spock/",
    },
    files: ["src/pages/rpsls.js", "src/utils/rpsls.js", "src/styles/rpsls.css"],
  },
  "/text-to-speech": {
    name: "Text to Speech",
    blurb: "The browser's own speech synthesis. No server, no key.",
    origin: {
      title: "text to speech api",
      date: "2021-03-01",
      path: "/blog/2021-03-01/text-to-speech-api/",
    },
    files: ["src/pages/text-to-speech.js", "src/styles/text-to-speech.css"],
  },
  "/warp": {
    name: "Warp",
    blurb: "A starfield at impulse, at warp, and stopped. Every star a point.",
    origin: {
      title: "star trek warp propulsion canvas",
      date: "2020-10-27",
      path: "/blog/2020-10-27/star-trek/",
    },
    files: [
      "src/pages/warp.js",
      "src/utils/starfield.js",
      "src/styles/warp.css",
    ],
  },
  "/crawl": {
    name: "Crawl",
    blurb: "The opening crawl in CSS 3D, with your words in it.",
    origin: {
      title: "star wars crawl",
      date: "2022-02-06",
      path: "/blog/2022-02-06/star-wars-crawl/",
    },
    files: ["src/pages/crawl.js", "src/styles/crawl.css"],
  },
  "/lunar-lander": {
    name: "Lunar Lander",
    blurb: "Land flat, slow, and on a pad. Thrust follows the nose.",
    origin: {
      title: "javascript lunar lander game",
      date: "2024-11-09",
      path: "/blog/2024-11-09/lunar-lander-game/",
    },
    files: [
      "src/pages/lunar-lander.js",
      "src/utils/lander.js",
      "src/styles/lunar-lander.css",
    ],
  },
  "/driving-sim": {
    name: "Parking Lot",
    blurb: "Drive around a Blockbuster lot and park it straight.",
    origin: {
      title: "blockbuster parking lot sim",
      date: "2025-04-26",
      path: "/blog/2025-04-26/driving-sim/",
    },
    files: [
      "src/pages/driving-sim.js",
      "src/utils/parkingLot.js",
      "src/styles/driving-sim.css",
    ],
  },
  "/gremlins": {
    name: "Gremlins",
    blurb: "Monkey testing: random input at a sandbox, until something breaks.",
    origin: {
      title: "gremlins",
      date: "2022-01-01",
      path: "/blog/2022-01-01/gremlins/",
    },
    files: [
      "src/pages/gremlins.js",
      "src/utils/gremlins.js",
      "src/styles/gremlins.css",
    ],
  },
}

/* Route to the filename its source bundle is written under. */
const sourceSlug = route => route.replace(/^\/+|\/+$/g, "") || "index"

const sourceUrlFor = route => `/app-source/${sourceSlug(route)}.json`

module.exports = { APPS, sourceSlug, sourceUrlFor }
